import { useState, useRef, useCallback } from "react";
import { type UserStory } from "@/lib/projectStore";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { GripVertical, CheckCircle2, Timer, Archive, Calculator, MessageSquare } from "lucide-react";
import CommentThread from "@/components/CommentThread";

function computeRice(s: UserStory["riceScore"]): number | null {
  if (!s || !s.effort) return null;
  return Math.round((s.reach * s.impact * (s.confidence / 100)) / s.effort);
}

const COLUMNS: { id: UserStory["status"]; label: string; icon: React.ComponentType<any>; emptyText: string }[] = [
  { id: "backlog", label: "Backlog", icon: Archive, emptyText: "Stories waiting to be picked up" },
  { id: "in-progress", label: "In Progress", icon: Timer, emptyText: "Work actively being done" },
  { id: "done", label: "Done", icon: CheckCircle2, emptyText: "Completed work this sprint" },
];

const priorityConfig: Record<string, { color: string; accent: string; label: string; icon: string }> = {
  must: { color: "border-l-destructive bg-destructive/10 border-destructive/20", accent: "text-destructive", label: "Must", icon: "🔴" },
  should: { color: "border-l-accent bg-accent/10 border-accent/20", accent: "text-accent", label: "Should", icon: "🟠" },
  could: { color: "border-l-blue-400 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800", accent: "text-blue-600 dark:text-blue-400", label: "Could", icon: "🔵" },
  wont: { color: "border-l-muted-foreground/50 bg-muted/40 border-border", accent: "text-muted-foreground", label: "Won't", icon: "⚪" },
};

interface Props {
  stories: UserStory[];
  onChange: (stories: UserStory[]) => void;
  projectId?: string;
  myRole?: "owner" | "editor" | "viewer";
  currentUserId?: string;
}

export default function KanbanBoard({ stories, onChange, projectId, myRole, currentUserId }: Props) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [view, setView] = useState<"board" | "compact">("board");
  const [commentsFor, setCommentsFor] = useState<string | null>(null);
  const dragRef = useRef<{ id: string; sourceCol: string } | null>(null);

  const grouped: Record<string, UserStory[]> = { backlog: [], "in-progress": [], done: [] };
  stories.forEach((s) => {
    const col = s.status || "backlog";
    if (grouped[col]) grouped[col].push(s);
    else grouped["backlog"].push(s);
  });

  const handleDragStart = (e: React.DragEvent, story: UserStory) => {
    setDragId(story.id);
    dragRef.current = { id: story.id, sourceCol: story.status };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", story.id);
  };

  const handleDragOver = (e: React.DragEvent, colId: string, idx: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverCol(colId);
    setDragOverIdx(idx);
  };

  const handleDragOverColumn = (e: React.DragEvent, colId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverCol(colId);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent, targetCol: string, targetIdx?: number) => {
      e.preventDefault();
      const storyId = e.dataTransfer.getData("text/plain") || dragId;
      if (!storyId) return;

      const newStatus = targetCol as UserStory["status"];
      const updated = stories.map((s) =>
        s.id === storyId ? { ...s, status: newStatus } : s
      );

      if (targetIdx !== undefined) {
        const colStories = updated.filter((s) => s.status === newStatus && s.id !== storyId);
        const movedStory = updated.find((s) => s.id === storyId);
        if (movedStory) {
          colStories.splice(targetIdx, 0, movedStory);
          const otherStories = updated.filter((s) => s.status !== newStatus);
          onChange([...otherStories, ...colStories]);
        } else {
          onChange(updated);
        }
      } else {
        onChange(updated);
      }

      setDragId(null);
      setDragOverCol(null);
      setDragOverIdx(null);
      dragRef.current = null;
    },
    [stories, onChange, dragId]
  );

  const handleDragEnd = () => {
    setDragId(null);
    setDragOverCol(null);
    setDragOverIdx(null);
    dragRef.current = null;
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Drag items between columns · {stories.length} items
        </p>
        <div className="flex border border-border rounded-md overflow-hidden">
          <button
            onClick={() => setView("board")}
            className={cn(
              "px-2.5 py-1 text-[10px] font-mono transition-colors",
              view === "board" ? "bg-accent text-accent-foreground font-bold" : "text-muted-foreground hover:bg-secondary"
            )}
          >
            Cards
          </button>
          <button
            onClick={() => setView("compact")}
            className={cn(
              "px-2.5 py-1 text-[10px] font-mono transition-colors",
              view === "compact" ? "bg-accent text-accent-foreground font-bold" : "text-muted-foreground hover:bg-secondary"
            )}
          >
            Compact
          </button>
        </div>
      </div>

      {stories.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm">
          Add user stories in the "User Stories" tab first.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-1 px-1">
        <div className="flex gap-3 min-h-[450px] min-w-[640px]">
          {COLUMNS.map((col) => {
            const items = grouped[col.id] || [];
            const isOver = dragOverCol === col.id;
            const ColIcon = col.icon;

            return (
              <div
                key={col.id}
                className={cn(
                  "flex-1 min-w-[240px] rounded-lg flex flex-col transition-all duration-200 border border-border/60",
                  isOver
                    ? "ring-2 ring-accent/40 bg-accent/5"
                    : col.id === "backlog" ? "bg-slate-50 dark:bg-slate-900/40"
                    : col.id === "in-progress" ? "bg-amber-50 dark:bg-amber-950/20"
                    : "bg-green-50 dark:bg-green-950/20"
                )}
                onDragOver={(e) => handleDragOverColumn(e, col.id)}
                onDrop={(e) => handleDrop(e, col.id)}
              >
                {/* Column header */}
                <div className={cn(
                  "px-4 py-3 border-b border-border/60 rounded-t-lg",
                  col.id === "backlog" ? "bg-slate-100 dark:bg-slate-800/60"
                  : col.id === "in-progress" ? "bg-amber-100 dark:bg-amber-900/40"
                  : "bg-green-100 dark:bg-green-900/40"
                )}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ColIcon className={cn("h-3.5 w-3.5",
                        col.id === "done" ? "text-green-600 dark:text-green-400"
                        : col.id === "in-progress" ? "text-amber-600 dark:text-amber-400"
                        : "text-slate-500"
                      )} />
                      <span className={cn("text-xs font-display font-bold tracking-wide",
                        col.id === "done" ? "text-green-800 dark:text-green-300"
                        : col.id === "in-progress" ? "text-amber-800 dark:text-amber-300"
                        : "text-slate-700 dark:text-slate-300"
                      )}>
                        {col.label}
                      </span>
                    </div>
                    <span className="text-[10px] font-mono text-muted-foreground bg-secondary/80 px-1.5 py-0.5 rounded">
                      {items.length}
                    </span>
                  </div>
                </div>

                {/* Cards */}
                <div className="flex-1 p-2 space-y-1.5 overflow-auto">
                  {items.map((story, idx) => {
                    const pc = priorityConfig[story.priority] || priorityConfig.could;
                    return (
                      <div
                        key={story.id}
                        onDragOver={(e) => handleDragOver(e, col.id, idx)}
                        onDrop={(e) => {
                          e.stopPropagation();
                          handleDrop(e, col.id, idx);
                        }}
                      >
                        {dragOverCol === col.id && dragOverIdx === idx && dragId !== story.id && (
                          <div className="h-0.5 bg-accent rounded-full mb-1 animate-fade-in" />
                        )}

                        {view === "compact" ? (
                          /* Compact row view */
                          <div
                            draggable
                            onDragStart={(e) => handleDragStart(e, story)}
                            onDragEnd={handleDragEnd}
                            className={cn(
                              "flex items-center gap-2 px-2.5 py-1.5 rounded border-l-2 cursor-grab active:cursor-grabbing transition-all text-xs",
                              pc.color,
                              dragId === story.id && "opacity-30 scale-95",
                              col.id === "done" && "opacity-70",
                              "hover:shadow-sm"
                            )}
                          >
                            <span className="text-[10px]">{pc.icon}</span>
                            <span className={cn("flex-1 font-body truncate", col.id === "done" && "line-through text-muted-foreground")}>
                              {story.title}
                            </span>
                            {story.storyPoints && (
                              <span className="text-[9px] font-mono text-muted-foreground shrink-0">
                                {story.storyPoints}p
                              </span>
                            )}
                          </div>
                        ) : (
                          /* Full card view */
                          <div
                            draggable
                            onDragStart={(e) => handleDragStart(e, story)}
                            onDragEnd={handleDragEnd}
                            className={cn(
                              "rounded-md p-3 cursor-grab active:cursor-grabbing transition-all border-l-[3px]",
                              pc.color,
                              "border border-border/40",
                              dragId === story.id && "opacity-30 scale-95",
                              col.id === "done" && "opacity-75",
                              "hover:shadow-md hover:translate-y-[-1px]"
                            )}
                          >
                            <div className="flex items-start gap-2">
                              <GripVertical className="h-3.5 w-3.5 text-muted-foreground/20 mt-0.5 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className={cn(
                                  "text-xs font-semibold text-card-foreground font-body leading-snug",
                                  col.id === "done" && "line-through text-muted-foreground"
                                )}>
                                  {story.title}
                                </p>
                                {story.asA && (
                                  <p className="text-[10px] text-muted-foreground mt-1 line-clamp-2 italic">
                                    As a {story.asA}, I want {story.iWant}
                                  </p>
                                )}
                                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                  <span className={cn("text-[9px] font-mono font-bold", pc.accent)}>
                                    {pc.icon} {pc.label}
                                  </span>
                                  {story.storyPoints && (
                                    <span className="text-[10px] font-mono text-foreground bg-secondary px-1.5 py-0.5 rounded-sm font-bold">
                                      {story.storyPoints}
                                    </span>
                                  )}
                                  {(() => { const r = computeRice(story.riceScore); return r !== null ? (
                                    <span className="text-[9px] font-mono text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-950/30 px-1 py-0.5 rounded-sm flex items-center gap-0.5">
                                      <Calculator className="h-2 w-2" />{r}
                                    </span>
                                  ) : null; })()}
                                  {(story.acceptanceCriteria?.length || 0) > 0 && (
                                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                      <CheckCircle2 className="h-2.5 w-2.5" />
                                      {story.acceptanceCriteria.length}
                                    </span>
                                  )}
                                  {(story.dependencies?.length || 0) > 0 && (
                                    <span className="text-[10px] text-accent/70">
                                      🔗 {story.dependencies!.length}
                                    </span>
                                  )}
                                  {projectId && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCommentsFor(commentsFor === story.id ? null : story.id);
                                      }}
                                      className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5 ml-auto"
                                      title="Comments"
                                    >
                                      <MessageSquare className="h-2.5 w-2.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {projectId && commentsFor === story.id && (
                          <CommentThread
                            projectId={projectId}
                            entityType="story"
                            entityId={story.id}
                            currentUserId={currentUserId}
                            myRole={myRole}
                            className="mt-1.5"
                          />
                        )}
                      </div>
                    );
                  })}

                  {items.length === 0 && (
                    <div
                      className={cn(
                        "flex-1 min-h-[120px] rounded-md border border-dashed border-border/30 flex flex-col items-center justify-center gap-1 text-center px-4",
                        isOver && "border-accent/50 bg-accent/5"
                      )}
                    >
                      <ColIcon className="h-5 w-5 text-muted-foreground/20" />
                      <span className="text-[10px] text-muted-foreground/40 font-body">{col.emptyText}</span>
                    </div>
                  )}

                  {items.length > 0 && dragOverCol === col.id && dragOverIdx === items.length && (
                    <div className="h-0.5 bg-accent rounded-full animate-fade-in" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}
    </div>
  );
}
